import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  Button,
  Divider,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Notifications as BellIcon,
  Announcement as AnnouncementIcon,
  Event as EventIcon,
  LocalHospital as HealthIcon,
  Forum as DiscussionIcon,
  Restaurant as RecipeIcon,
  Place as RecommendationIcon,
  FlagCircle as GoalIcon,
  PersonAdd as MemberIcon,
  Info as InfoIcon,
  CheckCircle as ReadIcon,
  Update as UpdateIcon,
  Build as MaintenanceIcon,
  Lightbulb as TipIcon,
  Warning as AlertIcon,
  Campaign as SystemAnnouncementIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../contexts/ProfileContext';
import {
  subscribeToNotifications,
  markAsRead,
  markAllAsRead,
  NOTIFICATION_TYPES
} from '../../services/notificationService';
import {
  subscribeToSystemNotifications,
  SYSTEM_NOTIFICATION_TYPES
} from '../../services/systemNotificationService';
import { formatDistanceToNow } from 'date-fns';
import logger from '../../utils/logger';

/**
 * NotificationBell Component
 * Displays notification icon with unread count and dropdown menu
 */
const NotificationBell = () => {
  const { currentUser } = useAuth();
  const { profile } = useProfile();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const [familyNotifications, setFamilyNotifications] = useState([]);
  const [systemNotifications, setSystemNotifications] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const open = Boolean(anchorEl);

  // Subscribe to real-time family notifications
  useEffect(() => {
    if (!profile?.familyCode || !currentUser?.uid) {
      return;
    }

    setLoading(true);
    setLoadError(false);

    // Safety net: if loading hasn't cleared after 8s, show empty state not spinner
    const loadingTimeout = setTimeout(() => {
      setLoading(false);
      setLoadError(true);
    }, 8000);

    const unsubscribe = subscribeToNotifications(
      profile.familyCode,
      currentUser.uid,
      (updatedNotifications) => {
        clearTimeout(loadingTimeout);
        setFamilyNotifications(updatedNotifications);
        setLoading(false);
        setLoadError(false);
      }
    );

    return () => unsubscribe();
  }, [profile?.familyCode, currentUser?.uid]);

  // Subscribe to real-time system notifications
  useEffect(() => {
    if (!currentUser?.uid) {
      return;
    }

    const unsubscribe = subscribeToSystemNotifications(
      currentUser.uid,
      profile?.familyCode,
      (updatedNotifications) => {
        setSystemNotifications(updatedNotifications);
      }
    );

    return () => unsubscribe();
  }, [currentUser?.uid, profile?.familyCode]);

  // Merge and sort family + system notifications
  useEffect(() => {
    const merged = [...familyNotifications, ...systemNotifications];

    // Sort by creation date (newest first)
    merged.sort((a, b) => {
      const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
      const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
      return bDate - aDate;
    });

    setNotifications(merged);

    // Calculate unread count (only family notifications are marked as read)
    const unread = familyNotifications.filter(n => !n.isRead).length + systemNotifications.length;
    setUnreadCount(unread);
  }, [familyNotifications, systemNotifications]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read (only for family notifications)
      if (!notification.isSystemNotification && !notification.isRead) {
        await markAsRead(profile.familyCode, notification.id);
      }

      // Navigate to action URL
      if (notification.actionUrl) {
        navigate(notification.actionUrl);
      }

      handleClose();
    } catch (error) {
      logger.error('Error handling notification click:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllAsRead(profile.familyCode, currentUser.uid);
    } catch (error) {
      logger.error('Error marking all as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    // System notification types
    switch (type) {
      case SYSTEM_NOTIFICATION_TYPES.UPDATE:
        return <UpdateIcon fontSize="small" color="primary" />;
      case SYSTEM_NOTIFICATION_TYPES.MAINTENANCE:
        return <MaintenanceIcon fontSize="small" color="warning" />;
      case SYSTEM_NOTIFICATION_TYPES.TIP:
        return <TipIcon fontSize="small" color="info" />;
      case SYSTEM_NOTIFICATION_TYPES.ANNOUNCEMENT:
        return <SystemAnnouncementIcon fontSize="small" color="primary" />;
      case SYSTEM_NOTIFICATION_TYPES.ALERT:
        return <AlertIcon fontSize="small" color="error" />;
      // Family notification types
      case NOTIFICATION_TYPES.ANNOUNCEMENT:
        return <AnnouncementIcon fontSize="small" color="primary" />;
      case NOTIFICATION_TYPES.EVENT:
        return <EventIcon fontSize="small" color="secondary" />;
      case NOTIFICATION_TYPES.HEALTH_EMERGENCY:
      case NOTIFICATION_TYPES.HEALTH_UPDATE:
        return <HealthIcon fontSize="small" color="error" />;
      case NOTIFICATION_TYPES.DISCUSSION:
        return <DiscussionIcon fontSize="small" color="info" />;
      case NOTIFICATION_TYPES.RECIPE:
        return <RecipeIcon fontSize="small" color="warning" />;
      case NOTIFICATION_TYPES.RECOMMENDATION:
        return <RecommendationIcon fontSize="small" color="success" />;
      case NOTIFICATION_TYPES.GOAL:
        return <GoalIcon fontSize="small" color="primary" />;
      case NOTIFICATION_TYPES.MEMBER_JOINED:
        return <MemberIcon fontSize="small" color="success" />;
      default:
        return <InfoIcon fontSize="small" color="action" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'error.main';
      case 'high':
        return 'warning.main';
      case 'medium':
        return 'info.main';
      case 'low':
      default:
        return 'text.secondary';
    }
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          color="inherit"
          onClick={handleOpen}
          aria-label="notifications"
          sx={{ minWidth: 48, minHeight: 48 }}
        >
          <Badge badgeContent={unreadCount} color="error">
            <BellIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 380,
            maxHeight: 500,
            mt: 1.5
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              onClick={handleMarkAllRead}
              startIcon={<ReadIcon />}
            >
              Mark all read
            </Button>
          )}
        </Box>

        <Divider />

        {/* Notifications List */}
        {loading && notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress size={24} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Loading notifications…
            </Typography>
          </Box>
        ) : loadError ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <BellIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Notifications are setting up
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Database indexes are building — ready in a few minutes
            </Typography>
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <BellIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
            {notifications.map((notification) => (
              <MenuItem
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                sx={{
                  py: 1.5,
                  px: 2,
                  borderLeft: 3,
                  borderColor: notification.isRead ? 'transparent' : getPriorityColor(notification.priority),
                  bgcolor: notification.isRead ? 'transparent' : 'action.hover',
                  '&:hover': {
                    bgcolor: 'action.selected'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {getNotificationIcon(notification.type)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: notification.isRead ? 400 : 600,
                        mb: 0.5
                      }}
                    >
                      {notification.title}
                    </Typography>
                  }
                  secondary={
                    <>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        {notification.message}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {notification.createdAt && notification.createdAt.toDate
                          ? formatDistanceToNow(notification.createdAt.toDate(), { addSuffix: true })
                          : 'Just now'}
                      </Typography>
                    </>
                  }
                />
              </MenuItem>
            ))}
          </Box>
        )}

        {notifications.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 1, textAlign: 'center' }}>
              <Button
                size="small"
                fullWidth
                onClick={() => {
                  navigate('/notifications');
                  handleClose();
                }}
              >
                View All Notifications
              </Button>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
};

export default NotificationBell;
